/**
 * Prestige Theme - Premium Shopify Theme for Luxury Brands
 * Author: Michael Benjamin Crowe
 * Version: 1.0.0
 */

(function() {
  'use strict';

  // Simple i18n helpers
  const STR = (window.theme && window.theme.strings) || {};
  const s = (key, fallback = '') => (typeof STR[key] === 'string' && STR[key]) || fallback || key;
  const interpolate = (str, ...vals) => typeof str === 'string' ? str.replace(/%s/g, () => vals.shift() ?? '') : undefined;
  const replacePlaceholders = (str, map) => {
    if (typeof str !== 'string') return undefined;
    return str.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (key in map ? map[key] : m));
  };
  const escapeHtml = (unsafe) => String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const Prestige = {
    settings: {
      animationDuration: 400,
      debounceDelay: 300,
      cartType: window.theme?.cartType || 'drawer'
    },

    init() {
      this.initHeader();
      this.initCart();
      this.initProductForms();
      this.initProductMedia();
      this.initModals();
      this.initSearch();
      this.initNewsletter();
      this.initWishlist();
      this.initQuickShop();
      this.initCollectionFilters();
      this.initAnimations();
      this.initAccessibility();
      this.initAnalyticsTracking();
    },

    initHeader() {
      const header = document.querySelector('.header');
      if (!header) return;

      const stickyHeader = header.classList.contains('header--sticky');
      const transparentHeader = header.classList.contains('header--transparent');

      if (stickyHeader) {
        let lastScroll = 0;
        window.addEventListener('scroll', this.debounce(() => {
          const currentScroll = window.pageYOffset;

          if (currentScroll > 100) {
            header.classList.add('is-scrolled');

            if (currentScroll > lastScroll && currentScroll > 300) {
              header.classList.add('is-hidden');
            } else {
              header.classList.remove('is-hidden');
            }
          } else {
            header.classList.remove('is-scrolled');
            header.classList.remove('is-hidden');
          }

          lastScroll = currentScroll;
        }, 10));
      }

      if (transparentHeader) {
        window.addEventListener('scroll', () => {
          if (window.pageYOffset > 50) {
            header.classList.add('is-filled');
          } else {
            header.classList.remove('is-filled');
          }
        });
      }

      const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
      const mobileMenu = document.querySelector('[data-mobile-menu]');

      // Guard: Mobile navigation drawer with backdrop overlay is handled comprehensively by sections/header.liquid
      if (mobileMenuToggle && mobileMenu && !document.querySelector('[data-mobile-menu-overlay]')) {
        mobileMenuToggle.addEventListener('click', () => {
          const isOpen = mobileMenu.classList.contains('is-open');
          mobileMenu.classList.toggle('is-open');
          document.body.classList.toggle('menu-open');
          mobileMenuToggle.setAttribute('aria-expanded', !isOpen);
        });
      }
    },

    initCart() {
      this.cartDrawer = document.querySelector('.cart-drawer');
      this.cartCount = document.querySelectorAll('[data-cart-count]');

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-toggle]')) {
          e.preventDefault();
          this.toggleCart();
        }

        if (e.target.closest('[data-cart-close]') || (e.target.matches && e.target.matches('[data-cart-overlay]'))) {
          e.preventDefault();
          this.closeCart();
        }
      });

      document.addEventListener('cart:updated', () => {
        this.updateCart();
      });

      // Synchronize cart state on page load
      this.updateCart();
    },

    toggleCart() {
      if (this.cartDrawer) {
        const isOpen = this.cartDrawer.classList.contains('is-open');
        if (isOpen) {
          this.closeCart();
        } else {
          this.openCart();
        }
      }
    },

    openCart() {
      if (this.cartDrawer) {
        // Store the currently focused element for restoration
        this.lastFocusedElement = document.activeElement;

        this.cartDrawer.classList.add('is-open');
        document.body.classList.add('cart-open');

        // Always sync with latest cart data on open
        this.updateCart();

        // Focus the drawer container smoothly without forcing focus outline on close button
        if (!this.cartDrawer.hasAttribute('tabindex')) {
          this.cartDrawer.setAttribute('tabindex', '-1');
        }
        this.cartDrawer.focus({ preventScroll: true });

        // Add focus trapping
        this.setupFocusTrap(this.cartDrawer);
      }
    },

    closeCart() {
      if (this.cartDrawer) {
        this.cartDrawer.classList.remove('is-open');
        document.body.classList.remove('cart-open');

        // Remove focus trap
        this.removeFocusTrap(this.cartDrawer);

        // Restore focus to the last focused element
        if (this.lastFocusedElement) {
          this.lastFocusedElement.focus();
        }
      }
    },

    async updateCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        this.updateCartCount(cart.item_count);
        this.updateCartDrawer(cart);

        if (cart.item_count === 0) {
          this.showEmptyCart();
        }
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    },

    updateCartCount(count) {
      this.cartCount.forEach(element => {
        if (element.closest('.cart-drawer__title')) {
          element.textContent = `(${count})`;
        } else {
          element.textContent = count;
          element.style.display = count > 0 ? 'flex' : 'none';
        }
      });
    },

    updateCartDrawer(cart) {
      if (!this.cartDrawer) return;

      const footer = this.cartDrawer.querySelector('.cart-drawer__footer');
      const upsellBox = this.cartDrawer.querySelector('#CartDrawerUpsellBox');
      const landmarkBox = this.cartDrawer.querySelector('#CartDrawerLandmarkBox');
      const freeShipWrap = this.cartDrawer.querySelector('#CartDrawerFreeShipWrap');

      if (!cart || cart.item_count === 0) {
        this.showEmptyCart();
        return;
      }

      if (footer) footer.style.display = '';
      if (upsellBox) upsellBox.style.display = '';
      if (landmarkBox) landmarkBox.style.display = '';
      if (freeShipWrap) freeShipWrap.style.display = '';

      const itemsContainer = this.cartDrawer.querySelector('[data-cart-items]');
      const subtotalElement = this.cartDrawer.querySelector('[data-cart-subtotal]');

      if (itemsContainer) {
        itemsContainer.innerHTML = cart.items.map(item => this.cartItemTemplate(item)).join('');
      }

      if (subtotalElement) {
        subtotalElement.textContent = this.formatMoney(cart.total_price);
      }

      this.updateFreeShippingBar(cart.total_price);

      const waBtn = this.cartDrawer.querySelector('#CartDrawerWhatsAppBtn');
      if (waBtn) {
        const phone = '923186639550';
        if (cart.items && cart.items.length > 0) {
          let msg = 'Salam Waseem Collection!\nI would like to place a Cash on Delivery (COD) order:\n\n';
          cart.items.forEach((item, index) => {
            const vTitle = item.variant_title && !item.variant_title.includes('Default') ? ` (${item.variant_title})` : '';
            msg += `${index + 1}. ${item.product_title}${vTitle} x ${item.quantity} - ${this.formatMoney(item.line_price)}\n`;
          });
          msg += `\n*Subtotal: ${this.formatMoney(cart.total_price)}*\nPayment: Cash on Delivery (COD)\nDelivery: Free Express Shipping (Open Parcel Allowed)\n\nPlease confirm my order and dispatch timeline!`;
          waBtn.href = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
        } else {
          waBtn.href = `https://wa.me/${phone}?text=${encodeURIComponent('Salam Waseem Collection! I have an inquiry regarding unstitched fabrics.')}`;
        }
      }
    },

    cartItemTemplate(item) {
      const imgSrc = item.image || (item.featured_image && item.featured_image.url) || '';
      const vTitle = item.variant_title && !item.variant_title.includes('Default') ? item.variant_title : '';
      return `
        <div class="cart-drawer__item" data-cart-item="${item.key}">
          <div class="cart-drawer__item-image">
            ${imgSrc ? `<img src="${imgSrc}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
          </div>
          <div class="cart-drawer__item-details">
            <h4 class="cart-drawer__item-title"><a href="${item.url || '#'}" style="text-decoration:none; color:inherit;">${escapeHtml(item.product_title)}</a></h4>
            ${vTitle ? `<p class="cart-drawer__item-variant">${escapeHtml(vTitle)}</p>` : ''}
            <p class="cart-drawer__item-price">${this.formatMoney(item.final_price != null ? item.final_price : item.price)}</p>
            <div class="cart-drawer__item-quantity">
              <button type="button" data-quantity-decrease data-item-key="${item.key}" aria-label="${s('decreaseQuantity', 'Decrease quantity')}">-</button>
              <input type="number" value="${item.quantity}" min="1" data-quantity-input data-item-key="${item.key}">
              <button type="button" data-quantity-increase data-item-key="${item.key}" aria-label="${s('increaseQuantity', 'Increase quantity')}">+</button>
            </div>
            <a href="#" class="cart-drawer__item-remove" data-cart-remove="${item.key}">${s('cartRemove', 'Remove')}</a>
          </div>
        </div>
      `;
    },

    showEmptyCart() {
      if (!this.cartDrawer) return;

      const itemsContainer = this.cartDrawer.querySelector('[data-cart-items]');
      if (itemsContainer) {
        itemsContainer.innerHTML = `
          <div class="cart-drawer__empty-state">
            <div class="empty-bag-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#B89758" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
            </div>
            <h3 class="empty-bag-title">Your Shopping Bag is Empty</h3>
            <p class="empty-bag-sub">Looks like you haven't added any luxury suits yet. Explore our fresh unstitched winter collection direct from Faisalabad.</p>
            <a href="/collections/all" class="button button--empty-shop" data-cart-close>
              <span>Explore Collections</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </a>
            <div class="empty-bag-links">
              <a href="/collections/ladies" class="empty-bag-link" data-cart-close>Ladies 3PC</a>
              <span class="dot">•</span>
              <a href="/collections/gents" class="empty-bag-link" data-cart-close>Gents Fabrics</a>
              <span class="dot">•</span>
              <a href="/collections/new-arrival" class="empty-bag-link" data-cart-close>New Drops</a>
            </div>
          </div>
        `;
      }

      const footer = this.cartDrawer.querySelector('.cart-drawer__footer');
      if (footer) footer.style.display = 'none';

      const upsellBox = this.cartDrawer.querySelector('#CartDrawerUpsellBox');
      if (upsellBox) upsellBox.style.display = 'none';

      const landmarkBox = this.cartDrawer.querySelector('#CartDrawerLandmarkBox');
      if (landmarkBox) landmarkBox.style.display = 'none';

      const freeShipWrap = this.cartDrawer.querySelector('#CartDrawerFreeShipWrap');
      if (freeShipWrap) freeShipWrap.style.display = 'none';
    },

    updateFreeShippingBar(totalPrice) {
      const bars = document.querySelectorAll('[data-wc-free-shipping-bar]');
      if (!bars.length) return;

      const tier1 = 300000; // Rs. 3,000
      const tier2 = 600000; // Rs. 6,000
      const tier3 = 900000; // Rs. 9,000

      bars.forEach(bar => {
        const progressEl = bar.querySelector('[data-free-shipping-progress]');
        const msgEl = bar.querySelector('[data-free-shipping-msg]');
        const step1 = bar.querySelector('#MilestoneStep1');
        const step2 = bar.querySelector('#MilestoneStep2');
        const step3 = bar.querySelector('#MilestoneStep3');

        let currentTier = 0;
        let amountNeeded = 0;
        let tierPercent = 0;

        if (totalPrice < tier1) {
          currentTier = 0;
          amountNeeded = tier1 - totalPrice;
          tierPercent = (totalPrice * 33.33) / tier1;
        } else if (totalPrice < tier2) {
          currentTier = 1;
          amountNeeded = tier2 - totalPrice;
          const tierSpan = tier2 - tier1;
          const tierProgress = totalPrice - tier1;
          tierPercent = 33.33 + (tierProgress * 33.33) / tierSpan;
        } else if (totalPrice < tier3) {
          currentTier = 2;
          amountNeeded = tier3 - totalPrice;
          const tierSpan = tier3 - tier2;
          const tierProgress = totalPrice - tier2;
          tierPercent = 66.66 + (tierProgress * 33.34) / tierSpan;
        } else {
          currentTier = 3;
          amountNeeded = 0;
          tierPercent = 100;
        }

        tierPercent = Math.min(100, Math.max(0, tierPercent));

        if (progressEl) {
          progressEl.style.width = `${tierPercent}%`;
        }

        if (step1) {
          if (currentTier >= 1) {
            step1.classList.add('is-active');
            const dot = step1.querySelector('.milestone-dot');
            if (dot) dot.textContent = '✓';
          } else {
            step1.classList.remove('is-active');
            const dot = step1.querySelector('.milestone-dot');
            if (dot) dot.textContent = '1';
          }
        }

        if (step2) {
          if (currentTier >= 2) {
            step2.classList.add('is-active');
            const dot = step2.querySelector('.milestone-dot');
            if (dot) dot.textContent = '✓';
          } else {
            step2.classList.remove('is-active');
            const dot = step2.querySelector('.milestone-dot');
            if (dot) dot.textContent = '2';
          }
        }

        if (step3) {
          if (currentTier >= 3) {
            step3.classList.add('is-active');
            const dot = step3.querySelector('.milestone-dot');
            if (dot) dot.textContent = '✓';
          } else {
            step3.classList.remove('is-active');
            const dot = step3.querySelector('.milestone-dot');
            if (dot) dot.textContent = '3';
          }
        }

        if (msgEl) {
          const neededFormatted = this.formatMoney(amountNeeded);
          if (currentTier === 0) {
            msgEl.innerHTML = `<span class="wc-free-ship-remaining"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B89758" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg><span>Add <strong>${neededFormatted}</strong> more for <strong>FREE Nationwide Delivery</strong></span></span>`;
          } else if (currentTier === 1) {
            msgEl.innerHTML = `<span class="wc-free-ship-tier2"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg><span>🎉 <strong>FREE Delivery Unlocked!</strong> Add <strong>${neededFormatted}</strong> for <strong>Rs. 500 OFF (Duo Offer)</strong>!</span></span>`;
          } else if (currentTier === 2) {
            msgEl.innerHTML = `<span class="wc-free-ship-tier3"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg><span>🏷️ <strong>Rs. 500 OFF Unlocked!</strong> Add <strong>${neededFormatted}</strong> for <strong>Rs. 1,000 OFF (Wardrobe Offer)</strong>!</span></span>`;
          } else {
            msgEl.innerHTML = `<span class="wc-free-ship-unlocked"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg><span>🌟 <strong>Mubarak! All Rewards Unlocked!</strong> (Free Delivery + Flat Rs. 1,000 OFF Applied)</span></span>`;
          }
        }
      });
    },

    initProductForms() {
      document.addEventListener('submit', async (e) => {
        if (e.target.matches('[data-product-form]')) {
          e.preventDefault();
          await this.handleAddToCart(e.target);
        }
      });

      document.addEventListener('change', (e) => {
        if (e.target.matches('[data-variant-select]')) {
          this.handleVariantChange(e.target);
        }
        // Fix Batch 2A: Cart drawer persistence (quantity)
        const qtyInput = e.target.closest('[data-quantity-input]');
        if (qtyInput) {
           const key = qtyInput.dataset.itemKey;
           const qty = parseInt(qtyInput.value, 10);
           const safeQty = isNaN(qty) ? 1 : Math.max(0, qty);
           fetch('/cart/change.js', {
             method: 'POST',
             headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
             body: JSON.stringify({ id: key, quantity: safeQty })
           }).then(r => r.json()).then(cart => {
             document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
             this.updateCart();
           }).catch(() => {
             this.updateCart();
           });
        }
      });

      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-option-value]')) {
          e.preventDefault();
          this.handleOptionClick(e.target);
        }

        const decBtn = e.target.closest('[data-quantity-decrease]');
        if (decBtn) {
          e.preventDefault();
          this.decreaseQuantity(decBtn);
        }

        const incBtn = e.target.closest('[data-quantity-increase]');
        if (incBtn) {
          e.preventDefault();
          this.increaseQuantity(incBtn);
        }

        // Cart drawer product removal fix
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          const key = removeBtn.dataset.cartRemove;
          const lineIndex = removeBtn.dataset.cartLine;
          
          // Immediate visual response
          const itemEl = removeBtn.closest('.cart-drawer__item') || removeBtn.closest('[data-cart-item]');
          if (itemEl) {
            itemEl.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
            itemEl.style.opacity = '0.3';
            itemEl.style.transform = 'scale(0.97)';
            itemEl.style.pointerEvents = 'none';
          }

          const removePayload = key ? { id: key, quantity: 0 } : { line: parseInt(lineIndex, 10), quantity: 0 };

          fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(removePayload)
          })
          .then(async (response) => {
            if (!response.ok) {
              console.warn('Direct remove failed, attempting line index fallback');
              const cartRes = await fetch('/cart.js');
              const cartData = await cartRes.json();
              const itemIdx = (cartData.items || []).findIndex(i => i.key === key || String(i.id) === String(key) || String(i.variant_id) === String(key));
              if (itemIdx !== -1) {
                return fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                  body: JSON.stringify({ line: itemIdx + 1, quantity: 0 })
                }).then(r => r.json());
              }
              return cartData;
            }
            return response.json();
          })
          .then((cart) => {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
            this.updateCart();
          })
          .catch((err) => {
            console.error('Error removing item from cart:', err);
            this.updateCart();
          });
        }
        
        // Fix Batch 2D: Quick-Add wiring
        const quickAddBtn = e.target.closest('[data-quick-add]');
        if (quickAddBtn) {
          e.preventDefault();
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: quickAddBtn.dataset.variantId || quickAddBtn.dataset.productId, quantity: 1 })
          }).then(r => r.json()).then(async () => {
             await this.updateCart();
             this.openCart();
          });
        }
      });
    },

    async handleAddToCart(form) {
      const submitButton = form.querySelector('[type="submit"]');
      const originalText = submitButton.textContent;

      submitButton.textContent = s('adding', 'Adding...');
      submitButton.disabled = true;

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const item = await response.json();

          submitButton.textContent = s('added', 'Added!');

          try {
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          } catch (e) {}

          if (this.settings.cartType === 'drawer') {
            this.openCart();
          }

          setTimeout(() => {
            submitButton.textContent = originalText;
            submitButton.disabled = false;
          }, 2000);

          document.dispatchEvent(new CustomEvent('cart:added', { detail: item }));

          // Universal AddToCart tracking for Meta Pixel, GA4, TikTok
          try {
            const trackPayload = {
              content_ids: [String(item.product_id || item.id)],
              content_name: item.product_title || item.title || '',
              content_type: 'product',
              value: (item.price ? item.price / 100 : 0) * (item.quantity || 1),
              currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ? window.Shopify.currency.active : 'PKR',
              num_items: item.quantity || 1
            };
            if (typeof window.trackEvent === 'function') {
              window.trackEvent('AddToCart', trackPayload);
            } else if (typeof fbq === 'function') {
              fbq('track', 'AddToCart', trackPayload);
            }
          } catch (tErr) {
            console.warn('AddToCart tracking notice:', tErr);
          }
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        submitButton.textContent = s('error', 'Error');

        setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
        }, 2000);
      }
    },

    handleVariantChange(select) {
      const form = select.closest('form');
      const selectedOption = select.options[select.selectedIndex];

      const price = selectedOption.dataset.price;
      const comparePrice = selectedOption.dataset.comparePrice;
      const available = selectedOption.dataset.available === 'true';
      const image = selectedOption.dataset.image;

      this.updateProductPrice(form, price, comparePrice);
      this.updateProductAvailability(form, available);

      if (image) {
        this.updateProductImage(image);
      }
    },

    handleOptionClick(option) {
      const group = option.closest('[data-option-group]');

      group.querySelectorAll('[data-option-value]').forEach(opt => {
        opt.classList.remove('is-active');
      });

      option.classList.add('is-active');

      const form = option.closest('form');
      const optionInputs = form.querySelectorAll('[data-option-input]');
      const optionName = option.dataset.optionName;
      const optionValue = option.dataset.optionValue;

      optionInputs.forEach(input => {
        if (input.name === optionName) {
          input.value = optionValue;
        }
      });

      this.updateSelectedVariant(form);
    },

    updateSelectedVariant(form) {
      const options = [];
      form.querySelectorAll('[data-option-input]').forEach(input => {
        options.push(input.value);
      });

      const variantTitle = options.join(' / ');
      const variantSelect = form.querySelector('[data-variant-select]');

      if (variantSelect) {
        const matchingOption = Array.from(variantSelect.options).find(option =>
          option.text === variantTitle
        );

        if (matchingOption) {
          variantSelect.value = matchingOption.value;
          this.handleVariantChange(variantSelect);
        }
      }
    },

    updateProductPrice(form, price, comparePrice) {
      const priceElement = form.closest('.product').querySelector('[data-product-price]');

      if (priceElement) {
        let priceHTML = this.formatMoney(price);

        if (comparePrice && comparePrice > price) {
          priceHTML = `
            <span class="product__price--sale">${this.formatMoney(price)}</span>
            <span class="product__price--compare">${this.formatMoney(comparePrice)}</span>
          `;
        }

        priceElement.innerHTML = priceHTML;
      }
    },

    updateProductAvailability(form, available) {
      const submitButton = form.querySelector('[type="submit"]');

      if (submitButton) {
        submitButton.disabled = !available;
        submitButton.textContent = available ? s('addToCart', 'Add to cart') : s('soldOut', 'Sold out');
      }
    },

    updateProductImage(imageSrc) {
      const mainImage = document.querySelector('[data-product-main-image]');

      if (mainImage) {
        mainImage.src = imageSrc;
      }
    },

    decreaseQuantity(button) {
      const input = button.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      const currentValue = parseInt(input.value, 10) || 1;

      if (currentValue > 1) {
        input.value = currentValue - 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // If quantity is already 1, pressing minus removes the item
        const itemContainer = button.closest('.cart-drawer__item') || button.closest('[data-cart-item]') || button.closest('[data-cart-item-row]');
        const removeBtn = itemContainer ? itemContainer.querySelector('[data-cart-remove], [data-cart-remove-btn]') : null;
        if (removeBtn) {
          removeBtn.click();
        }
      }
    },

    increaseQuantity(button) {
      const input = button.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      const currentValue = parseInt(input.value, 10) || 1;

      input.value = currentValue + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    },

    initProductMedia() {
      const productImages = document.querySelectorAll('[data-product-image]');
      const thumbnails = document.querySelectorAll('[data-product-thumbnail]');

      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', (e) => {
          e.preventDefault();

          const imageSrc = thumbnail.dataset.imageSrc;
          const mainImage = document.querySelector('[data-product-main-image]');

          if (mainImage) {
            mainImage.src = imageSrc;
          }

          thumbnails.forEach(thumb => thumb.classList.remove('is-active'));
          thumbnail.classList.add('is-active');
        });
      });

      productImages.forEach(image => {
        image.addEventListener('click', () => {
          this.openImageZoom(image.src);
        });

        image.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.openImageZoom(image.src);
          }
        });
      });
    },

    openImageZoom(imageSrc) {
      const modal = document.createElement('div');
      modal.className = 'modal image-zoom-modal is-open';
      modal.innerHTML = `
        <div class="modal__inner">
          <button class="modal__close" data-modal-close>&times;</button>
          <img src="${imageSrc}" alt="${s('zoomedImageAlt', 'Zoomed product image')}">
        </div>
      `;

      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        if (e.target === modal || e.target.matches('[data-modal-close]')) {
          modal.remove();
        }
      });
    },

    initModals() {
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-modal-open]')) {
          e.preventDefault();
          const modalId = e.target.dataset.modalOpen;
          this.openModal(modalId);
        }

        if (e.target.closest('[data-modal-close]')) {
          e.preventDefault();
          this.closeModal(e.target.closest('.modal'));
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const openModals = document.querySelectorAll('.modal.is-open');
          openModals.forEach(modal => this.closeModal(modal));
        }
      });
    },

    openModal(modalOrName) {
      const modal = typeof modalOrName === 'string'
        ? (document.querySelector(`[data-modal="${modalOrName}"]`) || document.querySelector(`[data-${modalOrName}-modal]`))
        : modalOrName;

      if (modal) {
        this.lastFocusedElement = document.activeElement;
        modal.classList.add('is-open');
        document.body.classList.add('modal-open');
        this.setupFocusTrap(modal);
        const input = modal.querySelector('input[type="search"], input:not([type="hidden"])');
        if (input) {
          setTimeout(() => input.focus(), 150);
        } else {
          const focusable = modal.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
          if (focusable.length) focusable[0].focus();
        }
      }
    },

    closeModal(modalOrName) {
      const modal = typeof modalOrName === 'string'
        ? (document.querySelector(`[data-modal="${modalOrName}"]`) || document.querySelector(`[data-${modalOrName}-modal]`))
        : (modalOrName || document.querySelector('.modal.is-open'));

      if (modal) {
        modal.classList.remove('is-open');
        document.body.classList.remove('modal-open');
        this.removeFocusTrap(modal);
        if (this.lastFocusedElement) {
          this.lastFocusedElement.focus();
        }
      }
    },

    trapFocus(modal) {
      this.setupFocusTrap(modal);
    },

    setupFocusTrap(modal) {
      if (!modal) return;
      const focusableElements = modal.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      modal.addEventListener('keydown', handleTabKey);
      modal._focusTrapHandler = handleTabKey;
    },

    removeFocusTrap(modal) {
      if (modal && modal._focusTrapHandler) {
        modal.removeEventListener('keydown', modal._focusTrapHandler);
        delete modal._focusTrapHandler;
      }
    },

    initSearch() {
      const searchToggle = document.querySelector('[data-search-toggle]');
      const searchModal = document.querySelector('[data-search-modal]');
      const searchForm = document.querySelector('[data-search-form]');
      const searchInput = document.querySelector('[data-search-input]');
      const searchResults = document.querySelector('[data-search-results]');

      if (searchToggle) {
        searchToggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal('search');
        });
      }

      // Close modal on close button, background click, or Escape key
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close]')) {
          e.preventDefault();
          this.closeModal();
        } else if (e.target.classList && e.target.classList.contains('modal') && e.target.classList.contains('is-open')) {
          this.closeModal();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModal();
        }
      });

      if (searchInput && searchResults) {
        searchInput.addEventListener('input', this.debounce(async (e) => {
          const query = e.target.value.trim();

          if (query.length >= 2) {
            await this.performSearch(query, searchResults);
          } else {
            searchResults.innerHTML = '';
          }
        }, this.settings.debounceDelay));
      }

      if (searchForm) {
        searchForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const query = searchInput.value.trim();

          if (query) {
            window.location.href = `/search?q=${encodeURIComponent(query)}&type=product&options[prefix]=last&sort_by=created-descending`;
          }
        });
      }
    },

    async performSearch(query, resultsContainer) {
      resultsContainer.innerHTML = '<div class="loading-spinner"></div>';

      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][prefix]=last`);
        const data = await response.json();

        if (data.resources && data.resources.results && data.resources.results.products && data.resources.results.products.length > 0) {
          const sortedProducts = [...data.resources.results.products].sort((a, b) => {
            const aAvail = a.available !== false && a.available !== 'false' ? 1 : 0;
            const bAvail = b.available !== false && b.available !== 'false' ? 1 : 0;
            return bAvail - aAvail;
          });
          const itemsHtml = sortedProducts.map(product => {
            const img = product.image || (product.featured_image && (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.url)) || '';
            const isAvail = product.available !== false && product.available !== 'false';
            return `
              <a href="${product.url}" class="search-result">
                ${img ? `<img src="${img}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<div class="search-result__no-img">🧵</div>'}
                <div class="search-result__details">
                  <h4>${escapeHtml(product.title)}</h4>
                  <p>${this.formatMoney(product.price)}${!isAvail ? ' <span class="sold-out-badge">(Sold Out)</span>' : ''}</p>
                </div>
              </a>
            `;
          }).join('');

          const viewAllHtml = `
            <a href="/search?q=${encodeURIComponent(query)}&type=product&options[prefix]=last&sort_by=created-descending" class="search-result__view-all">
              View all matching suits for &ldquo;${escapeHtml(query)}&rdquo; &rarr;
            </a>
          `;

          resultsContainer.innerHTML = itemsHtml + viewAllHtml;
        } else {
          const q = escapeHtml(query);
          const base = s('searchNoResults', 'No matching unstitched suits found');
          resultsContainer.innerHTML = `<p class="no-results">${base} ${q ? `for &quot;${q}&quot;` : ''}</p>`;
        }
      } catch (error) {
        console.error('Search error:', error);
        resultsContainer.innerHTML = `<p class="error">${s('genericError', 'An error occurred. Please try again.')}</p>`;
      }
    },

    initNewsletter() {
      const newsletterForms = document.querySelectorAll('[data-newsletter-form]');

      newsletterForms.forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          const email = form.querySelector('[type="email"]').value;
          const button = form.querySelector('[type="submit"]');
          const originalText = button.textContent;

          button.textContent = s('newsletterSubscribing', 'Subscribing...');
          button.disabled = true;

          try {
            const response = await fetch('/contact', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
              body: `form_type=customer&utf8=✓&contact[email]=${encodeURIComponent(email)}&contact[tags]=newsletter`
            });

            if (response.ok) {
              form.innerHTML = `<p class="success">${s('newsletterSuccess', 'Thank you for subscribing!')}</p>`;
            } else {
              throw new Error('Subscription failed');
            }
          } catch (error) {
            console.error('Newsletter error:', error);
            button.textContent = s('tryAgainError', 'Error - Please try again');

            setTimeout(() => {
              button.textContent = originalText;
              button.disabled = false;
            }, 3000);
          }
        });
      });
    },

    initWishlist() {
      const WISHLIST_KEY = 'prestige_wishlist';

      try {
        this.wishlist = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      } catch (err) {
        this.wishlist = [];
      }

      this.wishlistDrawer = document.querySelector('[data-wishlist-drawer]');

      this.updateWishlistButtons();
      this.updateWishlistCount();
      this.updateWishlistDrawer();

      document.addEventListener('click', (e) => {
        // 1. Wishlist Drawer Toggle (Header button or mobile menu button)
        if (e.target.closest('[data-wishlist-drawer-toggle]')) {
          e.preventDefault();
          this.toggleWishlistDrawer();
          return;
        }

        // 2. Wishlist Drawer Close (Close icon or backdrop overlay)
        if (e.target.closest('[data-wishlist-close]') || (e.target.matches && e.target.matches('[data-wishlist-overlay]'))) {
          e.preventDefault();
          this.closeWishlistDrawer();
          return;
        }

        // 3. Remove single item from Wishlist Drawer
        const drawerRemoveBtn = e.target.closest('[data-wishlist-drawer-remove]');
        if (drawerRemoveBtn) {
          e.preventDefault();
          const pId = String(drawerRemoveBtn.dataset.productId);
          const index = this.wishlist.findIndex(item => String(item.id) === pId);
          if (index > -1) {
            const removed = this.wishlist.splice(index, 1)[0];
            localStorage.setItem(WISHLIST_KEY, JSON.stringify(this.wishlist));
            this.updateWishlistButtons();
            this.updateWishlistCount();
            this.updateWishlistDrawer();
            this.updateWishlistPage();
            this.showNotification(`${removed.title || 'Item'} removed from wishlist`, 'info');
          }
          return;
        }

        // 4. Product Card Wishlist Toggle
        const toggleBtn = e.target.closest('[data-wishlist-toggle]');
        if (toggleBtn) {
          e.preventDefault();

          const productId = toggleBtn.dataset.productId;
          const productTitle = toggleBtn.dataset.productTitle;
          const productImage = toggleBtn.dataset.productImage;
          const productPrice = toggleBtn.dataset.productPrice;
          const productUrl = toggleBtn.dataset.productUrl;

          this.toggleWishlistItem({
            id: productId,
            title: productTitle,
            image: productImage,
            price: productPrice,
            url: productUrl
          });
          return;
        }
      });
    },

    toggleWishlistItem(product) {
      const index = this.wishlist.findIndex(item => String(item.id) === String(product.id));

      if (index > -1) {
        this.wishlist.splice(index, 1);
        this.showNotification(`${product.title} removed from wishlist`, 'info');
      } else {
        this.wishlist.push(product);
        this.showNotification(`${product.title} added to wishlist`, 'success');
      }

      localStorage.setItem('prestige_wishlist', JSON.stringify(this.wishlist));

      this.updateWishlistButtons();
      this.updateWishlistCount();
      this.updateWishlistPage();
      this.updateWishlistDrawer();
    },

    toggleWishlistDrawer() {
      if (this.wishlistDrawer) {
        if (this.wishlistDrawer.classList.contains('is-open')) {
          this.closeWishlistDrawer();
        } else {
          this.openWishlistDrawer();
        }
      }
    },

    openWishlistDrawer() {
      if (this.wishlistDrawer) {
        if (this.cartDrawer && this.cartDrawer.classList.contains('is-open')) {
          this.closeCart();
        }
        this.wishlistDrawer.classList.add('is-open');
        this.wishlistDrawer.setAttribute('aria-hidden', 'false');
        const overlay = document.querySelector('[data-wishlist-overlay]');
        if (overlay) overlay.classList.add('is-active');
        document.body.classList.add('wishlist-drawer-open');
        this.updateWishlistDrawer();
      }
    },

    closeWishlistDrawer() {
      if (this.wishlistDrawer) {
        this.wishlistDrawer.classList.remove('is-open');
        this.wishlistDrawer.setAttribute('aria-hidden', 'true');
        const overlay = document.querySelector('[data-wishlist-overlay]');
        if (overlay) overlay.classList.remove('is-active');
        document.body.classList.remove('wishlist-drawer-open');
      }
    },

    updateWishlistButtons() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(button => {
        const productId = String(button.dataset.productId);
        const isInWishlist = this.wishlist.some(item => String(item.id) === productId);

        button.classList.toggle('is-active', isInWishlist);
        button.setAttribute('aria-pressed', isInWishlist);

        const label = button.querySelector('.wishlist-btn-label');
        if (label) {
          label.textContent = isInWishlist ? 'Saved in Wishlist' : 'Save to Wishlist';
        }
      });
    },

    updateWishlistCount() {
      const count = this.wishlist ? this.wishlist.length : 0;
      document.querySelectorAll('[data-wishlist-count]').forEach(element => {
        element.textContent = count;
        if (element.classList.contains('site-header__wishlist-count')) {
          element.style.display = count > 0 ? 'flex' : 'none';
        } else {
          element.style.display = 'inline';
        }
      });
    },

    updateWishlistDrawer() {
      const drawerItems = document.querySelector('[data-wishlist-drawer-items]');
      if (!drawerItems) return;

      if (!this.wishlist || this.wishlist.length === 0) {
        drawerItems.innerHTML = `
          <div class="wishlist-drawer__empty">
            <div class="wishlist-drawer__empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" stroke-width="1.8">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
            <h3 class="wishlist-drawer__empty-title">Your Wishlist is Empty</h3>
            <p class="wishlist-drawer__empty-desc">Save your favorite unstitched suits by tapping the heart icon on any fabric.</p>
            <a href="/collections/all" class="wishlist-drawer__browse-btn" data-wishlist-close>Explore Suits</a>
          </div>
        `;
        return;
      }

      drawerItems.innerHTML = this.wishlist.map(product => `
        <div class="wishlist-drawer__item" data-product-id="${product.id}">
          <a href="${product.url}" class="wishlist-drawer__item-img">
            <img src="${product.image}" alt="${escapeHtml(product.title)}" loading="lazy">
          </a>
          <div class="wishlist-drawer__item-info">
            <a href="${product.url}" class="wishlist-drawer__item-title">${escapeHtml(product.title)}</a>
            <div class="wishlist-drawer__item-price">${product.price}</div>
            <div class="wishlist-drawer__item-actions">
              <a href="${product.url}" class="wishlist-drawer__item-view-btn">View Suit</a>
              <button type="button" class="wishlist-drawer__item-remove" data-wishlist-drawer-remove data-product-id="${product.id}" aria-label="Remove item">
                Remove
              </button>
            </div>
          </div>
        </div>
      `).join('');
    },

    updateWishlistPage() {
      const wishlistContainer = document.querySelector('[data-wishlist-items]');

      if (wishlistContainer) {
        if (this.wishlist.length > 0) {
          wishlistContainer.innerHTML = this.wishlist.map(product => `
            <div class="wishlist-item">
              <a href="${product.url}" class="wishlist-item__image">
                <img src="${product.image}" alt="${escapeHtml(product.title)}" loading="lazy">
              </a>
              <div class="wishlist-item__details">
                <h3><a href="${product.url}">${escapeHtml(product.title)}</a></h3>
                <p class="wishlist-item__price">${product.price}</p>
                <button data-wishlist-toggle data-product-id="${product.id}">
                  ${s('wishlistRemove', 'Remove from wishlist')}
                </button>
              </div>
            </div>
          `).join('');
        } else {
          wishlistContainer.innerHTML = `<p>${s('wishlistEmpty', 'Your wishlist is empty')}</p>`;
        }
      }
    },

    initQuickShop() {
      document.addEventListener('click', async (e) => {
        const directAddBtn = e.target.closest('[data-direct-add-to-cart]');
        if (directAddBtn) {
          e.preventDefault();
          const variantId = directAddBtn.dataset.variantId;
          const originalContent = directAddBtn.innerHTML;
          directAddBtn.innerHTML = '<span>Adding...</span>';
          directAddBtn.disabled = true;

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });

            if (response.ok) {
              
              this.openCart();
            }
          } catch (err) {
            console.error('Direct add to cart error:', err);
          } finally {
            directAddBtn.innerHTML = originalContent;
            directAddBtn.disabled = false;
          }
          return;
        }

        const quickShopBtn = e.target.closest('[data-quick-shop]');
        if (quickShopBtn) {
          e.preventDefault();
          const productHandle = quickShopBtn.dataset.productHandle;
          await this.openQuickShop(productHandle);
        }
      });
    },

    async openQuickShop(handle) {
      const modal = document.querySelector('[data-quick-shop-modal]');
      if (!modal) return;

      const content = modal.querySelector('[data-quick-shop-content]');
      const loadingHTML = '<div style="display:flex;align-items:center;justify-content:center;min-height:280px;"><div class="loading-spinner"></div></div>';
      content.innerHTML = loadingHTML;
      this.openModal('quick-shop');

      try {
        const response = await fetch('/products/' + handle + '.js');
        if (!response.ok) throw new Error('Product not found');
        const product = await response.json();

        const variant = product.variants.find(v => v.available) || product.variants[0];

        function formatMoney(cents) {
          return 'Rs. ' + (cents / 100).toLocaleString('en-PK', { minimumFractionDigits: 0 });
        }

        function getImageUrl(imgObjOrStr, size) {
          if (!imgObjOrStr) return '';
          var src = typeof imgObjOrStr === 'string' ? imgObjOrStr : (imgObjOrStr.src || '');
          if (!src) return '';
          if (src.indexOf('http') !== 0 && src.indexOf('//') === 0) {
            src = 'https:' + src;
          }
          if (src.indexOf('_' + size) !== -1) return src;
          return src.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, function(match, ext, query) {
            return '_' + size + '.' + ext + (query || '');
          });
        }

        // Color dictionary mapping for Pakistani & international fashion
        function getSwatchColor(val) {
          const v = String(val || '').toLowerCase().trim();
          if (v.includes('black') || v.includes('jet') || v.includes('siyah')) return '#121212';
          if (v.includes('white') || v.includes('snow') || v.includes('safaid')) return '#FFFFFF';
          if (v.includes('off white') || v.includes('off-white') || v.includes('offwhite') || v.includes('cream') || v.includes('ivory')) return '#F8F6F0';
          if (v.includes('navy') || v.includes('midnight') || v.includes('dark blue')) return '#1B2A4A';
          if (v.includes('royal blue') || v.includes('blue') || v.includes('neela')) return '#1D4ED8';
          if (v.includes('sky') || v.includes('ice blue') || v.includes('light blue') || v.includes('ferozi') || v.includes('firozi')) return '#38BDF8';
          if (v.includes('emerald') || v.includes('bottle') || v.includes('dark green')) return '#064E3B';
          if (v.includes('sea green')) return '#2E8B57';
          if (v.includes('olive') || v.includes('mehendi') || v.includes('mehndi')) return '#4D5D3B';
          if (v.includes('mint') || v.includes('pistachio') || v.includes('pista')) return '#6EE7B7';
          if (v.includes('green') || v.includes('sabz')) return '#15803D';
          if (v.includes('teal') || v.includes('peacock')) return '#0F766E';
          if (v.includes('maroon') || v.includes('burgundy') || v.includes('wine')) return '#58111A';
          if (v.includes('red') || v.includes('crimson') || v.includes('ruby') || v.includes('surkh') || v.includes('lal')) return '#991B1B';
          if (v.includes('rust') || v.includes('terracotta')) return '#9A3412';
          if (v.includes('mustard') || v.includes('gold') || v.includes('yellow') || v.includes('peela')) return '#EAB308';
          if (v.includes('brown') || v.includes('chocolate') || v.includes('coffee')) return '#451A03';
          if (v.includes('camel') || v.includes('khaki') || v.includes('tan') || v.includes('fawn')) return '#B45309';
          if (v.includes('beige') || v.includes('biscuit') || v.includes('sand') || v.includes('skin') || v.includes('nude')) return '#D7C4B7';
          if (v.includes('charcoal') || v.includes('dark grey') || v.includes('dark gray')) return '#374151';
          if (v.includes('grey') || v.includes('gray') || v.includes('silver') || v.includes('ash')) return '#9CA3AF';
          if (v.includes('zinc') || v.includes('steel')) return '#64748B';
          if (v.includes('copper') || v.includes('bronze')) return '#B87333';
          if (v.includes('plum') || v.includes('purple') || v.includes('violet') || v.includes('jamuni')) return '#581C87';
          if (v.includes('magenta') || v.includes('fuchsia')) return '#A21CAF';
          if (v.includes('tea pink')) return '#E8A598';
          if (v.includes('pink') || v.includes('rose') || v.includes('blush') || v.includes('gulabi')) return '#F472B6';
          if (v.includes('peach') || v.includes('apricot') || v.includes('coral')) return '#FB923C';
          if (v.includes('orange') || v.includes('narangi')) return '#EA580C';
          if (v.includes('lavender') || v.includes('lilac')) return '#A78BFA';
          if (v.includes('aqua') || v.includes('cyan')) return '#06B6D4';
          if (v.includes('lime')) return '#84CC16';
          return '';
        }

        let optionsHTML = '';
        if (!product.has_only_default_variant && product.options && product.options.length) {
          product.options.forEach(function(opt, optIdx) {
            const optionName = (typeof opt === 'object' && opt.name) ? opt.name : String(opt || 'Option');
            const values = [...new Set(product.variants.map(v => v.options[optIdx]))];
            const selectedVal = variant.options[optIdx];

            // Determine if this option is color/style/design or should have visual swatches
            const isVisualOption = /colou?r|shade|pattern|style|design|print|suit/i.test(optionName) ||
              values.some(v => getSwatchColor(v) !== '') ||
              product.variants.some(v => v.featured_image && v.options && v.options[optIdx]);

            optionsHTML += '<div class="quick-shop-modal__option-group">' +
              '<div class="quick-shop-modal__option-label">' +
                '<span>' + optionName + ':</span> <strong id="QSOptLbl' + optIdx + '" class="quick-shop-modal__option-val">' + selectedVal + '</strong>' +
              '</div>' +
              '<div class="quick-shop-modal__pills">';

            if (isVisualOption) {
              optionsHTML += values.map(function(val) {
                const active = val === selectedVal;
                const matchingVar = product.variants.find(function(v) {
                  return v.options && v.options[optIdx] === val;
                });
                const swatchColor = getSwatchColor(val);
                const varImg = matchingVar && matchingVar.featured_image ? getImageUrl(matchingVar.featured_image, '120x120') : '';

                let circleStyle = '';
                if (varImg) {
                  circleStyle = 'background-color:' + (swatchColor || '#FAF7F2') + ';background-image:url(\'' + varImg + '\');';
                } else if (swatchColor) {
                  circleStyle = 'background-color:' + swatchColor + ';';
                } else {
                  circleStyle = 'background-color:#EFEBE4;';
                }

                // If no image and not a recognized color, show short code inside circle (e.g. "01", "D-2")
                const innerText = (!varImg && !swatchColor && String(val).length <= 4) ? String(val) : '';

                return '<label class="quick-shop-modal__pill-label" title="' + val + '" aria-label="' + val + '">' +
                  '<input type="radio" name="qs_opt_' + optIdx + '" value="' + val + '" ' + (active ? 'checked' : '') +
                    ' data-qs-idx="' + optIdx + '" class="qs-r" style="position:absolute;opacity:0;width:0;height:0;pointer-events:none;">' +
                  '<span data-qs-pill="' + optIdx + '" class="quick-shop-modal__swatch-btn' + (active ? ' is-selected' : '') + '">' +
                    '<span class="quick-shop-modal__swatch-circle" style="' + circleStyle + '">' +
                      (innerText ? '<span>' + innerText + '</span>' : '') +
                      '<span class="quick-shop-modal__swatch-check">' +
                        '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
                      '</span>' +
                    '</span>' +
                  '</span>' +
                '</label>';
              }).join('');
            } else {
              // Regular text pills (Size, Stitching, etc.)
              optionsHTML += values.map(function(val) {
                const active = val === selectedVal;
                return '<label class="quick-shop-modal__pill-label" title="' + val + '" aria-label="' + val + '">' +
                  '<input type="radio" name="qs_opt_' + optIdx + '" value="' + val + '" ' + (active ? 'checked' : '') +
                    ' data-qs-idx="' + optIdx + '" class="qs-r" style="position:absolute;opacity:0;width:0;height:0;pointer-events:none;">' +
                  '<span data-qs-pill="' + optIdx + '" class="quick-shop-modal__pill' + (active ? ' is-selected' : '') + '">' +
                    val +
                  '</span>' +
                '</label>';
              }).join('');
            }

            optionsHTML += '</div></div>';
          });
        }

        // Price HTML
        const priceHTML = (variant.compare_at_price && variant.compare_at_price > variant.price)
          ? '<span id="QSPrice">' + formatMoney(variant.price) + '</span>' +
            '<span id="QSCompare">' + formatMoney(variant.compare_at_price) + '</span>' +
            '<span id="QSSave">SAVE ' + Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) + '%</span>'
          : '<span id="QSPrice">' + formatMoney(variant.price) + '</span>' +
            '<span id="QSCompare" style="display:none;"></span><span id="QSSave" style="display:none;"></span>';

        const waPhone = (modal && modal.dataset.waPhone) ? modal.dataset.waPhone : '923186639550';
        const waMsg = encodeURIComponent('Salam Waseem Collection! I want to order: ' + product.title + (variant.title && variant.title !== 'Default Title' ? ' [' + variant.title + ']' : '') + ' (' + formatMoney(variant.price) + '). Please confirm COD delivery.');
        const featImg = getImageUrl(variant.featured_image || (product.images && product.images[0]), '900x900') || (product.featured_image || '');

        // Thumbnails
        let thumbsHTML = '';
        if (product.images && product.images.length > 1) {
          thumbsHTML = '<div class="quick-shop-modal__thumbs">' +
            product.images.slice(0, 6).map(function(img, i) {
              const thumb = getImageUrl(img, '160x160');
              const big   = getImageUrl(img, '900x900');
              return '<img src="' + thumb + '" data-big="' + big + '" alt="" width="62" height="62" loading="lazy" class="quick-shop-modal__thumb' + (i === 0 ? ' is-active' : '') + '">';
            }).join('') +
          '</div>';
        }

        const html =
          '<script type="application/json" id="QSVars">' + JSON.stringify(product.variants) + '<\/script>' +
          '<div class="quick-shop-modal__product">' +
            // Left: Gallery
            '<div class="quick-shop-modal__gallery">' +
              '<div class="quick-shop-modal__main-img-wrap">' +
                '<img id="QSImg" src="' + featImg + '" alt="' + product.title.replace(/"/g,'&quot;') + '" loading="eager">' +
              '</div>' +
              thumbsHTML +
            '</div>' +
            // Right: Product Details & Options
            '<div class="quick-shop-modal__details">' +
              '<h2 class="quick-shop-modal__product-title">' + product.title + '</h2>' +
              '<div class="quick-shop-modal__price-box">' + priceHTML + '</div>' +
              optionsHTML +
              '<input type="hidden" id="QSVId" value="' + variant.id + '">' +
              '<div class="quick-shop-modal__qty-row">' +
                '<span class="quick-shop-modal__qty-label">Quantity:</span>' +
                '<div class="quick-shop-modal__stepper">' +
                  '<button type="button" class="qs-qty-btn" id="QSQtyMinus" aria-label="Decrease quantity">-</button>' +
                  '<input type="number" id="QSQty" value="1" min="1" class="qs-qty-input" aria-label="Item quantity" readonly>' +
                  '<button type="button" class="qs-qty-btn" id="QSQtyPlus" aria-label="Increase quantity">+</button>' +
                '</div>' +
              '</div>' +
              '<div class="quick-shop-modal__actions">' +
                '<button id="QSAdd" class="quick-shop-modal__atc-btn" onclick="(function(btn){' +
                  'var vid=document.getElementById(\'QSVId\').value;' +
                  'var qtyEl=document.getElementById(\'QSQty\');' +
                  'var qty=qtyEl?parseInt(qtyEl.value,10)||1:1;' +
                  'if(!vid)return;' +
                  'btn.disabled=true;btn.textContent=\'Adding...\';' +
                  'fetch(\'/cart/add.js\',{method:\'POST\',headers:{\'Content-Type\':\'application/json\'},body:JSON.stringify({id:parseInt(vid,10),quantity:qty})})' +
                  '.then(function(r){return r.json();})' +
                  '.then(function(){btn.textContent=\'Added \u2713\';' +
                    'document.dispatchEvent(new CustomEvent(\'cart:updated\',{bubbles:true}));' +
                    'fetch(\'/cart.js\').then(function(r){return r.json();}).then(function(c){document.dispatchEvent(new CustomEvent(\'cart:updated\',{bubbles:true,detail:{cart:c}}));' +
                      'setTimeout(function(){if(window.Prestige){window.Prestige.closeModal(\'quick-shop\');window.Prestige.openCart();}},400);' +
                    '});' +
                    'setTimeout(function(){btn.textContent=\'Add to Cart\';btn.disabled=false;},2000);})' +
                  '.catch(function(){btn.textContent=\'Error - try again\';btn.disabled=false;});' +
                '})(this)" ' + (variant.available ? '' : 'disabled') + '>' +
                (variant.available ? 'Add to Cart' : 'Sold Out') + '</button>' +
                '<a href="https://wa.me/' + waPhone + '?text=' + waMsg + '" target="_blank" rel="noopener" class="quick-shop-modal__wa-btn">' +
                  '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.711 2.592 2.654-.697c.969.529 1.777.818 2.806.818 3.182 0 5.768-2.586 5.768-5.766.001-3.18-2.585-5.766-5.768-5.766zm9.969 5.766c0 5.518-4.482 10-10 10-1.748 0-3.385-.45-4.819-1.238l-5.181 1.36 1.385-5.05c-.868-1.488-1.385-3.218-1.385-5.072 0-5.518 4.482-10 10-10s10 4.482 10 10z"/></svg>' +
                  '<span>Order via WhatsApp (COD)</span>' +
                '</a>' +
              '</div>' +
              '<a href="/products/' + handle + '" class="quick-shop-modal__details-link">View Full Details \u2192</a>' +
              '<div class="quick-shop-modal__trust-strip">' +
                '<span>\uD83D\uDCE6 Cash on Delivery</span>' +
                '<span>\uD83D\uDEE1\uFE0F Open Parcel Allowed</span>' +
                '<span>\uD83D\uDE9A Free Delivery above Rs.3,000</span>' +
              '</div>' +
            '</div>' +
          '</div>';

        content.innerHTML = html;

        // Wire up thumbnails
        content.querySelectorAll('.quick-shop-modal__thumb').forEach(function(thumb) {
          thumb.addEventListener('click', function() {
            var img = document.getElementById('QSImg');
            if (img && this.dataset.big) img.src = this.dataset.big;
            content.querySelectorAll('.quick-shop-modal__thumb').forEach(function(t){ t.classList.remove('is-active'); });
            this.classList.add('is-active');
          });
        });

        // Wire up variant radio pills (no page reload)
        var allVariants = product.variants;
        function getSelections() {
          var opts = [];
          if (product.options) {
            product.options.forEach(function(_, i) {
              var checked = content.querySelector('.qs-r[data-qs-idx="' + i + '"]:checked');
              opts[i] = checked ? checked.value : '';
            });
          }
          return opts;
        }
        function findVariant(opts) {
          return allVariants.find(function(v){ return v.options.every(function(o,i){ return o===opts[i]; }); }) || null;
        }

        // Wire up Quantity Stepper & WhatsApp sync
        var qtyInput = content.querySelector('#QSQty');
        var minusBtn = content.querySelector('#QSQtyMinus');
        var plusBtn = content.querySelector('#QSQtyPlus');

        function updateQSWhatsAppWithQty() {
          var waBtn = content.querySelector('.quick-shop-modal__wa-btn');
          if (!waBtn) return;
          var curVar = findVariant(getSelections()) || variant;
          var qVal = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
          var vName = curVar.title && curVar.title !== 'Default Title' ? ' [' + curVar.title + ']' : '';
          var totalCents = curVar.price * qVal;
          var newMsg = encodeURIComponent('Salam Waseem Collection! I want to order: ' + qVal + 'x ' + product.title + vName + ' (' + formatMoney(totalCents) + '). Please confirm COD delivery.');
          waBtn.href = 'https://wa.me/' + waPhone + '?text=' + newMsg;
        }

        if (minusBtn && plusBtn && qtyInput) {
          minusBtn.addEventListener('click', function(e) {
            e.preventDefault();
            var cur = parseInt(qtyInput.value, 10) || 1;
            if (cur > 1) {
              qtyInput.value = cur - 1;
              updateQSWhatsAppWithQty();
            }
          });
          plusBtn.addEventListener('click', function(e) {
            e.preventDefault();
            var cur = parseInt(qtyInput.value, 10) || 1;
            qtyInput.value = cur + 1;
            updateQSWhatsAppWithQty();
          });
        }

        content.addEventListener('change', function(e) {
          var radio = e.target.closest('.qs-r');
          if (!radio) return;
          var idx = parseInt(radio.dataset.qsIdx, 10);

          // Style pills
          content.querySelectorAll('[data-qs-pill="' + idx + '"]').forEach(function(pill) {
            var active = pill.previousElementSibling && pill.previousElementSibling.checked;
            if (active) {
              pill.classList.add('is-selected');
            } else {
              pill.classList.remove('is-selected');
            }
          });

          // Update label
          var lbl = document.getElementById('QSOptLbl' + idx);
          if (lbl) lbl.textContent = radio.value;

          var v = findVariant(getSelections());
          if (!v) return;

          // Update variant id
          var vidEl = document.getElementById('QSVId');
          if (vidEl) vidEl.value = v.id;

          // Update price
          var pEl = document.getElementById('QSPrice');
          var cEl = document.getElementById('QSCompare');
          var sEl = document.getElementById('QSSave');
          if (pEl) pEl.textContent = formatMoney(v.price);
          if (cEl && sEl) {
            if (v.compare_at_price && v.compare_at_price > v.price) {
              cEl.textContent = formatMoney(v.compare_at_price); cEl.style.display='';
              sEl.textContent = 'SAVE ' + Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) + '%'; sEl.style.display='';
            } else { cEl.style.display='none'; sEl.style.display='none'; }
          }

          // Update image
          if (v.featured_image) {
            var img = document.getElementById('QSImg');
            var newImgSrc = getImageUrl(v.featured_image, '900x900');
            if (img) img.src = newImgSrc;
            content.querySelectorAll('.quick-shop-modal__thumb').forEach(function(t) {
              if (t.dataset.big === newImgSrc) {
                t.classList.add('is-active');
              } else {
                t.classList.remove('is-active');
              }
            });
          }

          // Update WhatsApp link with variant and current quantity
          updateQSWhatsAppWithQty();

          // Update add button
          var addBtn = document.getElementById('QSAdd');
          if (addBtn) { addBtn.disabled = !v.available; addBtn.textContent = v.available ? 'Add to Cart' : 'Sold Out'; }
        });

      } catch (error) {
        console.error('Quick shop error:', error);
        content.innerHTML = '<div style="padding:2.5rem;text-align:center;"><p style="color:#A81D24;margin-bottom:1rem;font-weight:600;">Could not load product.</p><a href="/products/' + handle + '" style="font-weight:700;background:#121212;color:#fff;padding:10px 24px;border-radius:6px;text-decoration:none;">View Product Page</a></div>';
      }
    },
    initCollectionFilters() {
      const filterForm = document.querySelector('[data-filter-form]');
      const sortSelect = document.querySelector('[data-sort-select]');

      if (filterForm) {
        filterForm.addEventListener('change', this.debounce(() => {
          this.applyFilters(filterForm);
        }, this.settings.debounceDelay));

        filterForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.applyFilters(filterForm);
        });
      }

      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          const url = new URL(window.location);
          url.searchParams.set('sort_by', sortSelect.value);
          window.location = url.toString();
        });
      }

      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-filter-clear]')) {
          e.preventDefault();
          this.clearFilters();
        }

        if (e.target.matches('[data-filter-remove]')) {
          e.preventDefault();
          const filterId = e.target.dataset.filterId;
          this.removeFilter(filterId);
        }
      });
    },

    applyFilters(form) {
      const formData = new FormData(form);
      const params = new URLSearchParams(formData);

      const url = new URL(window.location);
      url.search = params.toString();

      this.loadFilteredProducts(url.toString());
    },

    clearFilters() {
      const url = new URL(window.location);
      url.search = '';

      this.loadFilteredProducts(url.toString());
    },

    removeFilter(filterId) {
      const url = new URL(window.location);
      url.searchParams.delete(filterId);

      this.loadFilteredProducts(url.toString());
    },

    async loadFilteredProducts(url) {
      const productsContainer = document.querySelector('[data-products-grid]');
      const loadingOverlay = document.querySelector('.loading-overlay');

      if (!productsContainer) return;

      loadingOverlay?.classList.add('is-loading');

      try {
        const response = await fetch(url);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newProducts = doc.querySelector('[data-products-grid]').innerHTML;
        const newFilters = doc.querySelector('[data-active-filters]')?.innerHTML;

        productsContainer.innerHTML = newProducts;

        const activeFilters = document.querySelector('[data-active-filters]');
        if (activeFilters && newFilters) {
          activeFilters.innerHTML = newFilters;
        }

        window.history.pushState({}, '', url);

        this.initAnimations();
      } catch (error) {
        console.error('Filter error:', error);
      } finally {
        loadingOverlay?.classList.remove('is-loading');
      }
    },

    initAnimations() {
      if (!window.IntersectionObserver) return;

      const animatedElements = document.querySelectorAll('[data-animate]');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animatedElements.forEach(element => {
        observer.observe(element);
      });
    },

    initAccessibility() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          document.body.classList.add('keyboard-nav');
        }
      });

      document.addEventListener('mousedown', () => {
        document.body.classList.remove('keyboard-nav');
      });

      const skipLink = document.querySelector('.skip-to-content-link');
      if (skipLink) {
        skipLink.addEventListener('click', (e) => {
          e.preventDefault();
          const target = document.querySelector(skipLink.getAttribute('href'));
          if (target) {
            target.focus();
            window.scrollTo({
              top: target.offsetTop,
              behavior: 'smooth'
            });
          }
        });
      }
    },

    showNotification(message, type = 'success') {
      const notification = document.createElement('div');
      notification.className = `notification notification--${type}`;
      notification.textContent = message;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.classList.add('is-visible');
      }, 10);

      setTimeout(() => {
        notification.classList.remove('is-visible');

        setTimeout(() => {
          notification.remove();
        }, this.settings.animationDuration);
      }, 3000);
    },

    initAnalyticsTracking() {
      // 1. InitiateCheckout tracking on any checkout or COD order trigger
      document.addEventListener('click', (e) => {
        const checkoutTarget = e.target.closest('a[href*="/checkout"], button[name="checkout"], .button--checkout, [data-checkout-trigger]');
        if (checkoutTarget) {
          try {
            if (typeof window.trackEvent === 'function') {
              window.trackEvent('InitiateCheckout', {});
            } else if (typeof fbq === 'function') {
              fbq('track', 'InitiateCheckout');
            }
          } catch (err) {
            console.warn('InitiateCheckout tracking notice:', err);
          }
        }
      });

      // 2. Contact / Lead event on WhatsApp order click
      document.addEventListener('click', (e) => {
        const waTarget = e.target.closest('a[href*="wa.me"], a[href*="whatsapp.com"], .btn-whatsapp, .button--whatsapp');
        if (waTarget) {
          try {
            if (typeof window.trackEvent === 'function') {
              window.trackEvent('Contact', { method: 'WhatsApp' });
            } else if (typeof fbq === 'function') {
              fbq('track', 'Contact');
            }
          } catch (err) {
            console.warn('WhatsApp tracking notice:', err);
          }
        }
      });
    },

    formatMoney(cents) {
      if (cents == null) return '';
      const format = window.theme?.moneyFormat || 'Rs. {{amount}}';
      const dollars = (cents / 100);
      const rounded = Math.round(dollars);
      const withCommas = (val) => val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      return format
        .replace(/\{\{\s*amount\s*\}\}/g, withCommas(dollars.toFixed(2)))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(rounded))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas(dollars.toFixed(2)))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas(rounded))
        .replace(/^\$/, 'Rs. ');
    },

    debounce(func, delay) {
      let timeoutId;
      return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    Prestige.init();
  });

  window.Prestige = Prestige;
})();
